import * as React from 'react';

// PACKAGES
import { useLocation } from 'react-router-dom';
import { SwitchTransition, CSSTransition } from 'react-transition-group';

// UTILS
import { queryStringToObject } from '../../utils';

// ICONS
import { ReactComponent as AlertIcon } from '../../assets/svg/alert.svg';
import { ReactComponent as CelebrateIcon } from '../../assets/svg/celebrate.svg';

// COMPONENTS
import LoadingSpinner from '../../components/LoadingSpinner';
import LoginForm from '../Login/LoginForm';
import ResetForm from './ResetForm';

// TYPES
import { AccountRegistrationCommand } from '../../api/models';

export interface UserInfoType {
	email?: string;
	token?: string;
	organisationIdentifier?: AccountRegistrationCommand['organisationIdentifier'];
	paymentPlanId?: AccountRegistrationCommand['paymentPlanId'];
	profileType?: AccountRegistrationCommand['profileType'];
}

interface ConfirmEmailTypes {}

export const ConfirmEmail: React.FC<ConfirmEmailTypes> = ({}) => {
	const location = useLocation();

	const [loading, setLoading] = React.useState<boolean>(true);
	const [tokenSuccess, setTokenSuccess] = React.useState<boolean>(false);
	const [resetSuccess, setResetSuccess] = React.useState<boolean>(false);
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [returnUrl, setReturnUrl] = React.useState<string | undefined>(undefined);

	const [userInfo, setUserInfo] = React.useState<UserInfoType>({
		email: '',
		token: '',
		organisationIdentifier: '',
		paymentPlanId: undefined,
		profileType: undefined
	});

	const decideReturnUrl = () => {
		let url = undefined;

		if (userInfo.organisationIdentifier) {
			url = '/join-organisation';
		} else if (userInfo.paymentPlanId) {
			url = '/register-organisation';
		}

		setReturnUrl(url);
	};

	const confirmToken = async () => {
		const locationParams = location.state;
		const queryParams = queryStringToObject();
		const params = { ...locationParams, ...queryParams };

		const data = {
			...userInfo,
			email: params.email || '',
			token: params.token || '',
			organisationIdentifier: params.organisationIdentifier || '',
			paymentPlanId: params.paymentPlanId || undefined,
			profileType: params.profileType || undefined
		};

		setLoading(true);

		if (data.token && data.email) {
			setUserInfo(data);
			setTokenSuccess(true);
		} else {
			setFormErrorMessage('An error has occurred \nPlease check your link and try again');
			setTokenSuccess(false);
		}

		setLoading(false);
	};

	React.useEffect(() => {
		decideReturnUrl();
	}, [userInfo]);

	React.useEffect(() => {
		confirmToken();
	}, []);

	return (
		<div className="page-reset-password">
			<div className="standalone-panel">
				<SwitchTransition mode="out-in">
					<CSSTransition
						key={`key_${loading}_${tokenSuccess}_${resetSuccess}`}
						timeout={600}
						classNames="standalone-fade"
						mountOnEnter
						unmountOnExit
					>
						{() => {
							// not using inline conditional for better readability

							if (loading) {
								return <LoadingSpinner show={loading} />;
							} else {
								// show error - no token in url
								if (!tokenSuccess && !resetSuccess) {
									return (
										<div className="confirm-error">
											<AlertIcon className="icon" />
											<p>{formErrorMessage}</p>
										</div>
									);
								}

								// show reset password
								if (tokenSuccess && !resetSuccess) {
									return (
										<div className="panel panel-reset">
											<div className="header">
												{/* <MainLogo className="logo" /> */}
												<h1 className="h2">Reset password</h1>
												<p className="sub-grey-text">Enter your new details below to reset your password</p>
											</div>
											<ResetForm token={userInfo.token} email={userInfo.email} callback={() => setResetSuccess(true)} />
										</div>
									);
								}

								// show login
								if (tokenSuccess && resetSuccess) {
									return (
										<div className="panel panel-login">
											<div className="header">
												<CelebrateIcon className="icon" />
												<h1 className="h1">Your password has been&nbsp;reset</h1>
											</div>
											<h2 className="h4">Login</h2>
											<LoginForm
												organisationIdentifier={userInfo.organisationIdentifier}
												paymentPlanId={userInfo.paymentPlanId}
												profileType={userInfo.profileType}
												returnUrl={returnUrl}
												emailAddress={userInfo.email}
											/>
										</div>
									);
								}
							}
						}}
					</CSSTransition>
				</SwitchTransition>
			</div>
		</div>
	);
};

export default ConfirmEmail;
